/**
 * Rótulos e helpers de apresentação da Persona (Brief 4 · Tela 2).
 *
 * Mapeia enums do serializer real (vertical/status/side/adversarial) para
 * rótulos PT-BR. `voiceSummary` deriva uma legenda de tom a partir do
 * `voiceProfile` REAL (formality · warmth) — é dado do serializer apenas
 * formatado, não fabricado (a coluna "Conv." e o "avatar" do gabarito são
 * omitidos por NÃO terem fonte no PersonaDto: zero dado falso).
 */

import type {
  PersonaAdversarial,
  PersonaSide,
  PersonaStatus,
  PersonaVertical,
  PersonaVoiceProfile,
} from './types';

export const VERTICAL_LABELS: Record<PersonaVertical, string> = {
  support: 'Suporte',
  commercial: 'Comercial',
  retention: 'Retenção',
  collections: 'Cobrança',
  triage: 'Triagem',
  onboarding: 'Onboarding',
  satisfaction_survey: 'Pesquisa de satisfação',
  legal: 'Jurídico',
  healthcare: 'Saúde',
  other: 'Outro',
};

export function verticalLabel(vertical: string): string {
  return VERTICAL_LABELS[vertical as PersonaVertical] ?? vertical;
}

export const STATUS_LABELS: Record<PersonaStatus, string> = {
  active: 'Ativo',
  draft: 'Rascunho',
  deprecated: 'Descontinuado',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as PersonaStatus] ?? status;
}

/**
 * Modificador visual do pill (reaproveita compounds: --active / --draft /
 * --inactive). `deprecated` reaproveita o visual neutro de `inactive`.
 */
export function statusPillModifier(status: string): 'active' | 'draft' | 'inactive' {
  if (status === 'active') return 'active';
  if (status === 'draft') return 'draft';
  return 'inactive';
}

/** Rótulo PT-BR do side default (ADR-041). Mantém o id canônico quando desconhecido. */
export const SIDE_LABELS: Record<PersonaSide, string> = {
  seller: 'Empresa',
  buyer: 'Cliente',
  neutral: 'Neutro',
  mediator: 'Mediador',
};

export function sideLabel(side: string): string {
  return SIDE_LABELS[side as PersonaSide] ?? side;
}

/** Rótulo PT-BR do adversarial profile default (ADR-040). */
export const ADVERSARIAL_LABELS: Record<PersonaAdversarial, string> = {
  none: 'Sem restrição',
  competitive: 'Competitivo',
  partial: 'Parcial',
  strict: 'Estrito',
};

export function adversarialLabel(adversarial: string): string {
  return ADVERSARIAL_LABELS[adversarial as PersonaAdversarial] ?? adversarial;
}

/**
 * Legenda curta de tom derivada do `voiceProfile` real (formalidade · acolhimento).
 * Capitaliza a 1ª letra de cada termo do serializer; ignora vazios. Sem dado →
 * string vazia (o chamador omite a linha).
 */
export function voiceSummary(voice: PersonaVoiceProfile): string {
  const parts = [voice.formality, voice.warmth]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter((p) => p.length > 0)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1));
  return parts.join(' · ');
}

/**
 * Rótulo PT-BR de cada dimensão do `voiceProfile` (formality/warmth/pace).
 * São as 3 dimensões reais do serializer (gabarito r5: formalidade · acolhimento
 * · ritmo); o VALOR já vem em PT no serializer — só formatamos (capitaliza).
 */
export const VOICE_DIMENSION_LABELS: Record<keyof PersonaVoiceProfile, string> = {
  formality: 'Formalidade',
  warmth: 'Acolhimento',
  pace: 'Ritmo',
};

/** Capitaliza a 1ª letra de um valor de voz do serializer; vazio → "—". */
export function voiceValueLabel(value: string): string {
  const v = typeof value === 'string' ? value.trim() : '';
  if (v === '') return '—';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

/**
 * Descrição honesta do side default (ADR-041): o que cada lado significa na
 * postura da IA. Copy de produto (não o scaffolding "(NetComet padrão)" do
 * protótipo). Sem fonte → string vazia (chamador omite a descrição).
 */
export const SIDE_DESCRIPTIONS: Record<PersonaSide, string> = {
  seller: 'A IA representa a empresa/operação nas respostas.',
  buyer: 'A IA representa o cliente (defesa do consumidor).',
  neutral: 'Postura tecnicamente neutra entre as partes.',
  mediator: 'A IA media entre as partes (uso especializado).',
};

export function sideDescription(side: string): string {
  return SIDE_DESCRIPTIONS[side as PersonaSide] ?? '';
}

/**
 * Descrição honesta do perfil adversarial default (ADR-040, 4 valores sem
 * prefix). `strict` bloqueia o modo H_c em conversas desta persona.
 */
export const ADVERSARIAL_DESCRIPTIONS: Record<PersonaAdversarial, string> = {
  none: 'Sem restrições de adversariedade.',
  competitive: 'Permite uso em vínculos competitivos.',
  partial: 'Aplica restrições parciais de adversariedade.',
  strict: 'Bloqueia o modo H_c em conversas desta persona.',
};

export function adversarialDescription(adversarial: string): string {
  return ADVERSARIAL_DESCRIPTIONS[adversarial as PersonaAdversarial] ?? '';
}
